import {
    graphqlClientIntegration,
    httpClientIntegration,
    init,
    reactRouterV6BrowserTracingIntegration,
    setTag,
} from '@sentry/react';
import { useEffect } from 'react';
import {
    createRoutesFromChildren,
    matchRoutes,
    useLocation,
    useNavigationType,
} from 'react-router';

import { env } from '~/lib/config';
import { store } from '~/store/store';

/**
 * Initializes Sentry when the environment variable VITE_APP_SENTRY_ENABLED is '1' and
 * either VITE_APP_SENTRY_DSN or `sentry_frontend_dsn` value from the features API REST
 * endpoint was provided.
 */
export function sentryInit() {
    const dsn =
        store.getState().features.values.sentry_frontend_dsn ||
        env.VITE_APP_SENTRY_DSN;

    if (env.VITE_APP_SENTRY_ENABLED !== '1' || !dsn) {
        return;
    }

    init({
        dsn,
        release: env.VITE_APP_SENTRY_RELEASE || undefined,
        environment: env.VITE_APP_ENVIRONMENT,
        beforeSend: function (event) {
            // Returning `null` means the event will not be reported

            const errorMessage =
                event.exception?.values?.[0]?.value?.toLowerCase();

            if (errorMessage?.includes('app.error.403.title')) {
                // 403 errors (permission issues) are an intentional scenario
                // which is why we will skip them.
                return null;
            }

            if (
                errorMessage?.includes(
                    'failed to fetch dynamically imported module',
                )
            ) {
                // We handle dynamic import errors in the ApplicationError class
                return null;
            }

            const frames = event.exception?.values?.[0]?.stacktrace?.frames;

            if (frames) {
                for (const frame of frames) {
                    if (
                        [
                            // Ignore errors in wmks and guacamole modules as they
                            // are third-party
                            'wmks',
                            'guacamole',
                        ].some(
                            (ignoredChunkBaseName) =>
                                frame?.filename?.startsWith(
                                    ignoredChunkBaseName,
                                ) ?? false,
                        )
                    ) {
                        return null;
                    }
                }
            }

            return event;
        },
        integrations: [
            httpClientIntegration(),
            graphqlClientIntegration({
                endpoints: [`${env.VITE_API_BASE_URL}/graphql`],
            }),
            reactRouterV6BrowserTracingIntegration({
                useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],
        tracesSampleRate: env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE
            ? parseFloat(env.VITE_APP_SENTRY_TRACES_SAMPLE_RATE)
            : 0,
        profilesSampleRate: env.VITE_APP_SENTRY_PROFILES_SAMPLE_RATE
            ? parseFloat(env.VITE_APP_SENTRY_PROFILES_SAMPLE_RATE)
            : 0,
        tracePropagationTargets: [env.VITE_APP_BASE_URL, env.VITE_API_BASE_URL],
    });

    setTag('service', 'smartlearn-app');
}
